import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useUnit } from 'effector-react';

import { integrationsApi } from '../api/integrations';
import { userHasRole } from '../config/roles';
import { showSoftSuccess } from '../utils/softSuccess';
import { $user } from '../store/auth';

const emptySettingsForm = {
  provider_code: 'billingservice-lorawan',
  name: 'BillingService LoRaWAN',
  api_base_url: '',
  api_token: '',
  enabled: false,
};

const emptyDiagnosticsForm = {
  enabled: true,
  uplink_retention_days: 30,
  gateway_buffer_retention_hours: 24,
  health_retention_days: 180,
  health_period_hours: 24,
  min_gateways: 1,
  rssi_warning: -115,
  snr_warning: -12,
};

const emptyBindingForm = {
  external_tenant_id: '',
  external_tenant_name: '',
  external_application_id: '',
  external_application_name: '',
  auto_create_enabled: true,
  sync_enabled: false,
};

const emptyMappingForm = {
  system_type: 'ХВС',
  decoder: '',
  manufacturer: '',
  external_device_profile_id: '',
  external_codec_profile_id: '',
  description: '',
  is_default: true,
};

const emptyApplicationForm = {
  name: '',
  external_application_id: '',
  external_application_name: '',
  system_type: '',
  decoder: '',
  manufacturer: '',
  is_default: false,
  sync_enabled: true,
};

const getErrorMessage = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const remoteProfileSourceLabel = (source) => (
  source === 'device_profile_template' ? 'Шаблон' : 'Профиль'
);

const LoRaWanSettingsPage = () => {
  const user = useUnit($user);
  const isSuperadmin = userHasRole(user, 'superadmin');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lorawanSettings, setLorawanSettings] = useState(null);
  const [lorawanForm, setLorawanForm] = useState(emptySettingsForm);
  const [diagnosticsSettings, setDiagnosticsSettings] = useState(null);
  const [diagnosticsForm, setDiagnosticsForm] = useState(emptyDiagnosticsForm);
  const [lorawanTest, setLorawanTest] = useState(null);
  const [lorawanTenants, setLorawanTenants] = useState([]);
  const [lorawanMappings, setLorawanMappings] = useState([]);
  const [mappingForm, setMappingForm] = useState(emptyMappingForm);
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [remoteProfiles, setRemoteProfiles] = useState([]);
  const [remoteProfilesLoading, setRemoteProfilesLoading] = useState(false);
  const [bindingDialog, setBindingDialog] = useState(null);
  const [bindingForm, setBindingForm] = useState(emptyBindingForm);
  const [tenantApplications, setTenantApplications] = useState([]);
  const [applicationForm, setApplicationForm] = useState(emptyApplicationForm);
  const [editingApplicationId, setEditingApplicationId] = useState(null);
  const [gatewayBuffer, setGatewayBuffer] = useState([]);
  const [gatewayBufferLoading, setGatewayBufferLoading] = useState(false);
  const [gatewayBufferEventType, setGatewayBufferEventType] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const configuredBindings = useMemo(
    () => lorawanTenants.filter((item) => item.sync_enabled || item.external_tenant_id || item.external_application_id).length,
    [lorawanTenants]
  );

  const cleanupTotals = useMemo(() => {
    if (!cleanupResult) return null;
    const tenantsTotal = (cleanupResult.tenants || []).reduce(
      (acc, item) => acc + item.events + item.observations + item.downlinks + item.device_health_snapshots + item.gateway_health_snapshots + (item.coverage_daily || 0),
      0
    );
    return {
      tenantsTotal,
      gatewayEvents: cleanupResult.public_buffer?.gateway_events || 0,
      schemas: cleanupResult.tenants?.length || 0,
    };
  }, [cleanupResult]);

  const loadLoRaWan = useCallback(async () => {
    if (!isSuperadmin) return;
    setError('');
    try {
      const [settings, diagnostics, tenants, mappings, buffer] = await Promise.all([
        integrationsApi.getLoRaWanSettings(),
        integrationsApi.getLoRaWanDiagnosticsSettings(),
        integrationsApi.listLoRaWanTenantBindings(),
        integrationsApi.listLoRaWanProfileMappings(),
        integrationsApi.listLoRaWanGatewayBuffer({ limit: 50 }),
      ]);
      setLorawanSettings(settings);
      setDiagnosticsSettings(diagnostics);
      setLorawanTenants(Array.isArray(tenants) ? tenants : []);
      setLorawanMappings(Array.isArray(mappings) ? mappings : []);
      setGatewayBuffer(Array.isArray(buffer) ? buffer : []);
      setLorawanForm({
        provider_code: settings?.provider_code || emptySettingsForm.provider_code,
        name: settings?.name || emptySettingsForm.name,
        api_base_url: settings?.api_base_url || '',
        api_token: '',
        enabled: Boolean(settings?.enabled),
      });
      setDiagnosticsForm({
        enabled: diagnostics?.enabled !== false,
        uplink_retention_days: diagnostics?.uplink_retention_days || emptyDiagnosticsForm.uplink_retention_days,
        gateway_buffer_retention_hours: diagnostics?.gateway_buffer_retention_hours || emptyDiagnosticsForm.gateway_buffer_retention_hours,
        health_retention_days: diagnostics?.health_retention_days || emptyDiagnosticsForm.health_retention_days,
        health_period_hours: diagnostics?.health_period_hours || emptyDiagnosticsForm.health_period_hours,
        min_gateways: diagnostics?.min_gateways || emptyDiagnosticsForm.min_gateways,
        rssi_warning: diagnostics?.rssi_warning ?? emptyDiagnosticsForm.rssi_warning,
        snr_warning: diagnostics?.snr_warning ?? emptyDiagnosticsForm.snr_warning,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить настройки LoRaWAN'));
    }
  }, [isSuperadmin]);

  useEffect(() => {
    loadLoRaWan();
  }, [loadLoRaWan]);

  const loadGatewayBuffer = useCallback(async () => {
    if (!isSuperadmin) return;
    setGatewayBufferLoading(true);
    setError('');
    try {
      const data = await integrationsApi.listLoRaWanGatewayBuffer({
        event_type: gatewayBufferEventType || undefined,
        limit: 50,
      });
      setGatewayBuffer(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить gateway buffer'));
    } finally {
      setGatewayBufferLoading(false);
    }
  }, [gatewayBufferEventType, isSuperadmin]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    setLorawanTest(null);
    try {
      const payload = {
        provider_code: lorawanForm.provider_code.trim() || emptySettingsForm.provider_code,
        name: lorawanForm.name.trim() || emptySettingsForm.name,
        api_base_url: lorawanForm.api_base_url.trim() || null,
        enabled: lorawanForm.enabled,
      };
      if (lorawanForm.api_token.trim()) {
        payload.api_token = lorawanForm.api_token.trim();
      }
      const updated = await integrationsApi.updateLoRaWanSettings(payload);
      setLorawanSettings(updated);
      setLorawanForm((prev) => ({ ...prev, api_token: '' }));
      setSuccess('Сохранено');
      showSoftSuccess('Сохранено');
      await loadLoRaWan();
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить настройки LoRaWAN'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const result = await integrationsApi.testLoRaWanSettings();
      setLorawanTest(result);
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось проверить настройки LoRaWAN'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDiagnosticsSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        enabled: diagnosticsForm.enabled,
        uplink_retention_days: Number(diagnosticsForm.uplink_retention_days),
        gateway_buffer_retention_hours: Number(diagnosticsForm.gateway_buffer_retention_hours),
        health_retention_days: Number(diagnosticsForm.health_retention_days),
        health_period_hours: Number(diagnosticsForm.health_period_hours),
        min_gateways: Number(diagnosticsForm.min_gateways),
        rssi_warning: Number(diagnosticsForm.rssi_warning),
        snr_warning: Number(diagnosticsForm.snr_warning),
      };
      const updated = await integrationsApi.updateLoRaWanDiagnosticsSettings(payload);
      setDiagnosticsSettings(updated);
      setDiagnosticsForm({
        enabled: updated.enabled !== false,
        uplink_retention_days: updated.uplink_retention_days,
        gateway_buffer_retention_hours: updated.gateway_buffer_retention_hours,
        health_retention_days: updated.health_retention_days,
        health_period_hours: updated.health_period_hours,
        min_gateways: updated.min_gateways,
        rssi_warning: updated.rssi_warning,
        snr_warning: updated.snr_warning,
      });
      setSuccess('Настройки диагностики сохранены');
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить настройки диагностики'));
    } finally {
      setSaving(false);
    }
  };

  const handleDiagnosticsCleanup = async (dryRun = true) => {
    if (!dryRun && !window.confirm('Удалить устаревшие диагностические события LoRaWAN? Основные данные приборов не затрагиваются.')) {
      return;
    }
    setCleanupLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await integrationsApi.cleanupLoRaWanDiagnostics({
        dry_run: dryRun,
        confirm_cleanup: !dryRun,
        all_tenants: true,
        include_gateway_buffer: true,
        uplink_retention_days: Number(diagnosticsForm.uplink_retention_days),
        health_retention_days: Number(diagnosticsForm.health_retention_days),
      });
      setCleanupResult(result);
      setSuccess(dryRun ? 'Проверка очистки выполнена' : 'Устаревшая диагностика очищена');
      if (!dryRun) {
        await loadGatewayBuffer();
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось выполнить очистку диагностики'));
    } finally {
      setCleanupLoading(false);
    }
  };

  const resetApplicationForm = () => {
    setApplicationForm(emptyApplicationForm);
    setEditingApplicationId(null);
  };

  const loadTenantApplications = async (tenantId) => {
    const data = await integrationsApi.listLoRaWanTenantApplications(tenantId);
    setTenantApplications(Array.isArray(data) ? data : []);
  };

  const openBindingDialog = async (tenant) => {
    setBindingDialog(tenant);
    setBindingForm({
      external_tenant_id: tenant.external_tenant_id || '',
      external_tenant_name: tenant.external_tenant_name || '',
      external_application_id: tenant.external_application_id || '',
      external_application_name: tenant.external_application_name || '',
      auto_create_enabled: tenant.auto_create_enabled !== false,
      sync_enabled: Boolean(tenant.sync_enabled),
    });
    resetApplicationForm();
    setTenantApplications([]);
    setRemoteProfiles([]);
    try {
      await loadTenantApplications(tenant.tenant_id);
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить application клиента'));
    }
  };

  const handleSaveBinding = async () => {
    if (!bindingDialog) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await integrationsApi.updateLoRaWanTenantBinding(bindingDialog.tenant_id, {
        provider_settings_id: lorawanSettings?.id || null,
        external_tenant_id: bindingForm.external_tenant_id.trim() || null,
        external_tenant_name: bindingForm.external_tenant_name.trim() || null,
        external_application_id: bindingForm.external_application_id.trim() || null,
        external_application_name: bindingForm.external_application_name.trim() || null,
        auto_create_enabled: bindingForm.auto_create_enabled,
        sync_enabled: bindingForm.sync_enabled,
      });
      setBindingDialog(null);
      setSuccess('Привязка клиента сохранена');
      await loadLoRaWan();
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить привязку клиента'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditApplication = (application) => {
    setEditingApplicationId(application.id);
    setApplicationForm({
      name: application.name || '',
      external_application_id: application.external_application_id || '',
      external_application_name: application.external_application_name || '',
      system_type: application.system_type || '',
      decoder: application.decoder || '',
      manufacturer: application.manufacturer || '',
      is_default: Boolean(application.is_default),
      sync_enabled: application.sync_enabled !== false,
    });
  };

  const handleSaveApplication = async () => {
    if (!bindingDialog) return;
    if (!applicationForm.name.trim() || !applicationForm.external_application_id.trim()) {
      setError('Укажите название и Application ID.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        provider_settings_id: lorawanSettings?.id || null,
        name: applicationForm.name.trim(),
        external_application_id: applicationForm.external_application_id.trim(),
        external_application_name: applicationForm.external_application_name.trim() || null,
        system_type: applicationForm.system_type.trim() || null,
        decoder: applicationForm.decoder.trim() || null,
        manufacturer: applicationForm.manufacturer.trim() || null,
        is_default: applicationForm.is_default,
        sync_enabled: applicationForm.sync_enabled,
      };
      if (editingApplicationId) {
        await integrationsApi.updateLoRaWanTenantApplication(editingApplicationId, payload);
      } else {
        await integrationsApi.createLoRaWanTenantApplication(bindingDialog.tenant_id, payload);
      }
      resetApplicationForm();
      await loadTenantApplications(bindingDialog.tenant_id);
      setSuccess(editingApplicationId ? 'Application обновлён' : 'Application добавлен');
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить application'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!bindingDialog) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await integrationsApi.deleteLoRaWanTenantApplication(applicationId);
      if (editingApplicationId === applicationId) {
        resetApplicationForm();
      }
      await loadTenantApplications(bindingDialog.tenant_id);
      setSuccess('Application удалён');
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось удалить application'));
    } finally {
      setSaving(false);
    }
  };

  const resetMappingForm = () => {
    setMappingForm(emptyMappingForm);
    setEditingMappingId(null);
  };

  const handleEditMapping = (mapping) => {
    setEditingMappingId(mapping.id);
    setMappingForm({
      system_type: mapping.system_type || emptyMappingForm.system_type,
      decoder: mapping.decoder || '',
      manufacturer: mapping.manufacturer || '',
      external_device_profile_id: mapping.external_device_profile_id || '',
      external_codec_profile_id: mapping.external_codec_profile_id || '',
      description: mapping.description || '',
      is_default: Boolean(mapping.is_default),
    });
  };

  const handleSaveMapping = async () => {
    if (!lorawanSettings?.id) {
      setError('Сначала сохраните настройки BillingService LoRaWAN.');
      return;
    }
    if (!mappingForm.system_type.trim()) {
      setError('Укажите тип прибора для mapping.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        provider_settings_id: lorawanSettings.id,
        system_type: mappingForm.system_type.trim(),
        decoder: mappingForm.decoder.trim() || null,
        manufacturer: mappingForm.manufacturer.trim() || null,
        external_device_profile_id: mappingForm.external_device_profile_id.trim() || null,
        external_codec_profile_id: mappingForm.external_codec_profile_id.trim() || null,
        description: mappingForm.description.trim() || null,
        is_default: mappingForm.is_default,
      };
      if (editingMappingId) {
        await integrationsApi.updateLoRaWanProfileMapping(editingMappingId, payload);
      } else {
        await integrationsApi.createLoRaWanProfileMapping(payload);
      }
      resetMappingForm();
      setSuccess(editingMappingId ? 'Profile/codec mapping обновлен' : 'Profile/codec mapping добавлен');
      await loadLoRaWan();
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить profile/codec mapping'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await integrationsApi.deleteLoRaWanProfileMapping(mappingId);
      if (editingMappingId === mappingId) {
        resetMappingForm();
      }
      setSuccess('Profile/codec mapping удален');
      await loadLoRaWan();
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось удалить profile/codec mapping'));
    } finally {
      setSaving(false);
    }
  };

  const handleLoadRemoteProfiles = async () => {
    setRemoteProfilesLoading(true);
    setError('');
    setSuccess('');
    const externalTenantId = bindingForm.external_tenant_id.trim();
    if (!externalTenantId) {
      setRemoteProfilesLoading(false);
      setError('Укажите Tenant ID клиента.');
      return;
    }
    try {
      const data = await integrationsApi.listLoRaWanRemoteProfiles(externalTenantId);
      setRemoteProfiles(Array.isArray(data) ? data : []);
      setSuccess(data?.length ? `Загружено профилей: ${data.length}` : 'Профили не найдены');
    } catch (err) {
      setRemoteProfiles([]);
      setError(getErrorMessage(err, 'Не удалось загрузить профили LoRaWAN'));
    } finally {
      setRemoteProfilesLoading(false);
    }
  };

  const handleUseRemoteProfile = (profile) => {
    setMappingForm((prev) => ({
      ...prev,
      manufacturer: profile.vendor || prev.manufacturer,
      external_device_profile_id: profile.external_id || prev.external_device_profile_id,
      external_codec_profile_id: profile.source === 'device_profile_template' ? profile.external_id : prev.external_codec_profile_id,
      description: profile.name || profile.description || prev.description,
    }));
    setSuccess('Профиль подставлен в форму mapping.');
  };

  if (!isSuperadmin) {
    return (
      <Box className="home-container page-animate" sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1280, mx: 'auto' }}>
        <Alert severity="info">Управление BillingService LoRaWAN доступно только суперадминистратору.</Alert>
      </Box>
    );
  }

  return (
    <Box
      className="home-container page-animate"
      sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1280, mx: 'auto', width: '100%' }}
    >
      <Stack spacing={2.25}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Paper
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LanRoundedIcon color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    BillingService LoRaWAN
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Системная настройка подключения и привязок клиентов.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={lorawanSettings?.enabled ? 'Включено' : 'Выключено'}
                  color={lorawanSettings?.enabled ? 'success' : 'default'}
                  variant="outlined"
                />
                <Chip size="small" label={`Клиентов: ${configuredBindings}/${lorawanTenants.length}`} variant="outlined" />
              </Stack>
            </Stack>

            <Divider />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="Название"
                size="small"
                value={lorawanForm.name}
                onChange={(event) => setLorawanForm((prev) => ({ ...prev, name: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Код провайдера"
                size="small"
                value={lorawanForm.provider_code}
                onChange={(event) => setLorawanForm((prev) => ({ ...prev, provider_code: event.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="Endpoint"
                size="small"
                value={lorawanForm.api_base_url}
                onChange={(event) => setLorawanForm((prev) => ({ ...prev, api_base_url: event.target.value }))}
                fullWidth
              />
              <TextField
                label={lorawanSettings?.has_api_token ? `API-токен (${lorawanSettings.api_token_masked})` : 'API-токен'}
                size="small"
                type="password"
                value={lorawanForm.api_token}
                onChange={(event) => setLorawanForm((prev) => ({ ...prev, api_token: event.target.value }))}
                placeholder={lorawanSettings?.has_api_token ? 'Оставьте пустым, чтобы не менять' : ''}
                fullWidth
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={lorawanForm.enabled}
                    onChange={(event) => setLorawanForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                  />
                }
                label="Интеграция включена"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<SyncRoundedIcon />}
                  onClick={handleTestSettings}
                  disabled={saving || !lorawanSettings}
                >
                  Проверить
                </Button>
                <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={handleSaveSettings} disabled={saving}>
                  Сохранить
                </Button>
              </Stack>
            </Stack>
            {lorawanTest && <Alert severity={lorawanTest.ok ? 'success' : 'warning'}>{lorawanTest.message}</Alert>}
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, md: 2.25 },
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
          }}
        >
          <Stack spacing={1.75}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
              <Stack spacing={0.35}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Диагностика сети
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Пороги и сроки хранения для фоновой диагностики.
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={diagnosticsSettings?.enabled === false ? 'Выключена' : 'Включена'}
                color={diagnosticsSettings?.enabled === false ? 'default' : 'success'}
                variant="outlined"
                sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
              />
            </Stack>
            <Divider />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                type="number"
                label="Uplink retention, дней"
                value={diagnosticsForm.uplink_retention_days}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, uplink_retention_days: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Gateway buffer, часов"
                value={diagnosticsForm.gateway_buffer_retention_hours}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, gateway_buffer_retention_hours: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Агрегаты, дней"
                value={diagnosticsForm.health_retention_days}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, health_retention_days: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Период, часов"
                value={diagnosticsForm.health_period_hours}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, health_period_hours: event.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                size="small"
                type="number"
                label="Мин. БС"
                value={diagnosticsForm.min_gateways}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, min_gateways: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="RSSI warning"
                value={diagnosticsForm.rssi_warning}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, rssi_warning: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="SNR warning"
                value={diagnosticsForm.snr_warning}
                onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, snr_warning: event.target.value }))}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={diagnosticsForm.enabled}
                    onChange={(event) => setDiagnosticsForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                  />
                }
                label="Включена"
                sx={{ whiteSpace: 'nowrap' }}
              />
              <Button
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                onClick={handleSaveDiagnosticsSettings}
                disabled={saving}
                sx={{ minWidth: 140 }}
              >
                Сохранить
              </Button>
            </Stack>
            <Divider />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack spacing={0.25}>
                <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
                  Очистка диагностики
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Удаляются только устаревшие события диагностики и gateway buffer.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={() => handleDiagnosticsCleanup(true)}
                  disabled={cleanupLoading}
                  startIcon={<SyncRoundedIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Проверить
                </Button>
                <Button
                  color="warning"
                  variant="outlined"
                  onClick={() => handleDiagnosticsCleanup(false)}
                  disabled={cleanupLoading || !cleanupResult}
                  startIcon={<DeleteOutlineRoundedIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Очистить
                </Button>
              </Stack>
            </Stack>
            {cleanupResult ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid rgba(38, 65, 94, 0.10)',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Chip
                  size="small"
                  label={cleanupResult.dry_run ? 'Проверка' : 'Очищено'}
                  color={cleanupResult.dry_run ? 'info' : 'success'}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
                <Chip size="small" label={`Схем: ${cleanupTotals?.schemas || 0}`} variant="outlined" />
                <Chip size="small" label={`Tenant-строк: ${cleanupTotals?.tenantsTotal || 0}`} variant="outlined" />
                <Chip size="small" label={`Gateway buffer: ${cleanupTotals?.gatewayEvents || 0}`} variant="outlined" />
              </Stack>
            ) : null}
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: 0,
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Stack spacing={0.25}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Буфер gateway-событий
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Короткое хранение gateway-событий без содержимого payload.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                select
                size="small"
                label="Тип события"
                value={gatewayBufferEventType}
                onChange={(event) => setGatewayBufferEventType(event.target.value)}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">Все</MenuItem>
                {['event/up', 'event/ack', 'event/stats', 'command/down', 'state/conn'].map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<SyncRoundedIcon />}
                onClick={loadGatewayBuffer}
                disabled={gatewayBufferLoading}
              >
                Обновить
              </Button>
            </Stack>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table stickyHeader size="small" sx={{ minWidth: 1060 }}>
                <TableHead>
                  <TableRow>
                    {['Время', 'Gateway ID', 'Тип', 'Context', 'Размер', 'Payload hash', 'Привязка', 'Хранить до'].map((title) => (
                      <TableCell key={title} sx={{ fontWeight: 800, color: 'text.secondary', backgroundColor: '#F7FAFE' }}>
                        {title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gatewayBuffer.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{formatDate(item.received_at)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.gateway_id}</TableCell>
                      <TableCell>
                        <Chip size="small" label={item.event_type} variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.context || '—'}</TableCell>
                      <TableCell>{item.payload_size} Б</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {item.payload_hash.slice(0, 16)}…
                      </TableCell>
                      <TableCell>
                        {item.matched_tenant_schema ? (
                          <Chip size="small" label={item.matched_tenant_schema} color="success" variant="outlined" />
                        ) : (
                          <Chip size="small" label="Не привязано" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(item.expires_at)}</TableCell>
                    </TableRow>
                  ))}
                  {!gatewayBuffer.length ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        Событий пока нет.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        <Paper
          sx={{
            p: 0,
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Stack spacing={0.25}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Клиенты
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Привязка tenant/application для клиентов.
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <Box component="thead">
                <Box component="tr" sx={{ backgroundColor: 'color-mix(in oklab, var(--surface-muted) 76%, transparent)' }}>
                  {['Клиент', 'Tenant', 'Applications', 'Auto-create', 'Sync', 'Статус', 'Действия'].map((title) => (
                    <Box
                      key={title}
                      component="th"
                      sx={{ px: 2, py: 1.35, textAlign: 'left', fontSize: 13, fontWeight: 700, color: 'text.secondary' }}
                    >
                      {title}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {lorawanTenants.map((tenant) => (
                  <Box
                    component="tr"
                    key={tenant.tenant_id}
                    sx={{
                      borderTop: '1px solid color-mix(in oklab, var(--border-color) 72%, transparent)',
                      '&:hover': { backgroundColor: 'color-mix(in oklab, var(--surface-muted) 58%, transparent)' },
                    }}
                  >
                    <Box component="td" sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{tenant.tenant_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tenant.tenant_schema}</Typography>
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontFamily: 'monospace', fontSize: 13 }}>
                      {tenant.external_tenant_id || '—'}
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontFamily: 'monospace', fontSize: 13 }}>
                      {tenant.applications_count ? `${tenant.applications_count} шт.` : tenant.external_application_id || '—'}
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5 }}>
                      <Chip size="small" label={tenant.auto_create_enabled ? 'Да' : 'Нет'} variant="outlined" />
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5 }}>
                      <Chip
                        size="small"
                        label={tenant.sync_enabled ? 'Включено' : 'Выключено'}
                        color={tenant.sync_enabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5 }}>
                      <Tooltip title={tenant.last_sync_error || ''}>
                        <Chip size="small" label={tenant.last_sync_status || 'Не настроено'} variant="outlined" />
                      </Tooltip>
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5 }}>
                      <Button size="small" variant="outlined" onClick={() => openBindingDialog(tenant)}>
                        Настроить
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
            backgroundColor: 'var(--surface-bg)',
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
              <Stack spacing={0.25}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Профили устройств
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mapping для выбора profile/codec при синхронизации устройств.
                </Typography>
              </Stack>
              <Chip size="small" label={`Записей: ${lorawanMappings.length}`} variant="outlined" />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <TextField
                select
                size="small"
                label="Тип прибора"
                value={mappingForm.system_type}
                onChange={(event) => setMappingForm((prev) => ({ ...prev, system_type: event.target.value }))}
                sx={{ minWidth: 150 }}
              >
                {['ХВС', 'ГВС', 'Отопление', 'Электросчётчик', 'УРТ'].map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Decoder"
                value={mappingForm.decoder}
                onChange={(event) => setMappingForm((prev) => ({ ...prev, decoder: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                label="Производитель"
                value={mappingForm.manufacturer}
                onChange={(event) => setMappingForm((prev) => ({ ...prev, manufacturer: event.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                label="Device profile ID"
                value={mappingForm.external_device_profile_id}
                onChange={(event) => setMappingForm((prev) => ({ ...prev, external_device_profile_id: event.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                label="Codec profile ID"
                value={mappingForm.external_codec_profile_id}
                onChange={(event) => setMappingForm((prev) => ({ ...prev, external_codec_profile_id: event.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                size="small"
                label="Описание"
                value={mappingForm.description}
                onChange={(event) => setMappingForm((prev) => ({ ...prev, description: event.target.value }))}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={mappingForm.is_default}
                    onChange={(event) => setMappingForm((prev) => ({ ...prev, is_default: event.target.checked }))}
                  />
                }
                label="По умолчанию"
                sx={{ whiteSpace: 'nowrap' }}
              />
              <Stack direction="row" spacing={1}>
                {editingMappingId && (
                  <Button variant="outlined" onClick={resetMappingForm} disabled={saving}>
                    Отмена
                  </Button>
                )}
                <Button variant="contained" onClick={handleSaveMapping} disabled={saving || !lorawanSettings?.id}>
                  {editingMappingId ? 'Сохранить' : 'Добавить'}
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={0.75}>
              {lorawanMappings.length ? lorawanMappings.map((mapping) => (
                <Box
                  key={mapping.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr 1fr 1fr auto auto' },
                    gap: 1,
                    alignItems: 'center',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid rgba(38, 65, 94, 0.10)',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>{mapping.system_type}</Typography>
                  <Typography variant="body2" color="text.secondary">{mapping.decoder || 'Любой decoder'}</Typography>
                  <Typography variant="body2" color="text.secondary">{mapping.manufacturer || 'Любой производитель'}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{mapping.external_device_profile_id || '—'}</Typography>
                  <Chip size="small" label={mapping.is_default ? 'Default' : 'Custom'} variant="outlined" />
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Редактировать">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditMapping(mapping)}
                        disabled={saving}
                        sx={{ minWidth: 36, px: 1 }}
                      >
                        <EditRoundedIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => handleDeleteMapping(mapping.id)}
                        disabled={saving}
                        sx={{ minWidth: 36, px: 1 }}
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>
              )) : (
                <Typography variant="body2" color="text.secondary">
                  Mapping пока не добавлен.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={Boolean(bindingDialog)}
        onClose={() => {
          if (!saving) setBindingDialog(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Привязка клиента</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
            <Stack spacing={2}>
              <Alert severity="info">
              {bindingDialog?.tenant_name || 'Клиент'}: настройте внешний tenant и application для разных типов приборов, производителей или decoder.
            </Alert>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="Tenant ID"
                value={bindingForm.external_tenant_id}
                onChange={(event) => setBindingForm((prev) => ({ ...prev, external_tenant_id: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Tenant name"
                value={bindingForm.external_tenant_name}
                onChange={(event) => setBindingForm((prev) => ({ ...prev, external_tenant_name: event.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="Application ID"
                value={bindingForm.external_application_id}
                onChange={(event) => setBindingForm((prev) => ({ ...prev, external_application_id: event.target.value }))}
                fullWidth
                helperText="Legacy fallback. Для новых настроек используйте список application ниже."
              />
              <TextField
                label="Application name"
                value={bindingForm.external_application_name}
                onChange={(event) => setBindingForm((prev) => ({ ...prev, external_application_name: event.target.value }))}
                fullWidth
                helperText="Legacy fallback"
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bindingForm.auto_create_enabled}
                    onChange={(event) => setBindingForm((prev) => ({ ...prev, auto_create_enabled: event.target.checked }))}
                  />
                }
                label="Создавать автоматически"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={bindingForm.sync_enabled}
                    onChange={(event) => setBindingForm((prev) => ({ ...prev, sync_enabled: event.target.checked }))}
                  />
                }
                label="Синхронизация включена"
              />
            </Stack>
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: '1px solid rgba(38, 65, 94, 0.10)',
                backgroundColor: 'rgba(248, 250, 252, 0.75)',
              }}
            >
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                  <Stack spacing={0.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Профили этого клиента
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Загрузка выполняется по указанному Tenant ID.
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SyncRoundedIcon />}
                    onClick={handleLoadRemoteProfiles}
                    disabled={saving || remoteProfilesLoading || !lorawanSettings?.enabled || !bindingForm.external_tenant_id.trim()}
                  >
                    {remoteProfilesLoading ? 'Загрузка...' : 'Подтянуть профили'}
                  </Button>
                </Stack>

                {remoteProfiles.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1,
                      maxHeight: 240,
                      overflow: 'auto',
                    }}
                  >
                    {remoteProfiles.map((profile) => (
                      <Box
                        key={`${profile.source}-${profile.external_id}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: 1,
                          alignItems: 'center',
                          px: 1.25,
                          py: 1,
                          borderRadius: 2,
                          border: '1px solid rgba(38, 65, 94, 0.10)',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip size="small" label={remoteProfileSourceLabel(profile.source)} variant="outlined" />
                            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                              {profile.name || profile.external_id}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }} noWrap>
                            {profile.external_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[profile.vendor, profile.region, profile.codec_runtime].filter(Boolean).join(' · ') || 'Без дополнительных параметров'}
                          </Typography>
                        </Stack>
                        <Button size="small" variant="outlined" onClick={() => handleUseRemoteProfile(profile)}>
                          В mapping
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={1.25}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Applications клиента
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                <TextField
                  size="small"
                  label="Название"
                  value={applicationForm.name}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Application ID"
                  value={applicationForm.external_application_id}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, external_application_id: event.target.value }))}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Application name"
                  value={applicationForm.external_application_name}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, external_application_name: event.target.value }))}
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                <TextField
                  select
                  size="small"
                  label="Тип прибора"
                  value={applicationForm.system_type}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, system_type: event.target.value }))}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="">Любой тип</MenuItem>
                  {['ХВС', 'ГВС', 'Отопление', 'Электросчётчик', 'УРТ'].map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Decoder"
                  value={applicationForm.decoder}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, decoder: event.target.value }))}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Производитель"
                  value={applicationForm.manufacturer}
                  onChange={(event) => setApplicationForm((prev) => ({ ...prev, manufacturer: event.target.value }))}
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={applicationForm.is_default}
                      onChange={(event) => setApplicationForm((prev) => ({ ...prev, is_default: event.target.checked }))}
                    />
                  }
                  label="По умолчанию"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={applicationForm.sync_enabled}
                      onChange={(event) => setApplicationForm((prev) => ({ ...prev, sync_enabled: event.target.checked }))}
                    />
                  }
                  label="Включено"
                />
                {editingApplicationId && (
                  <Button variant="outlined" onClick={resetApplicationForm} disabled={saving}>
                    Отмена
                  </Button>
                )}
                <Button variant="contained" onClick={handleSaveApplication} disabled={saving || !lorawanSettings?.id}>
                  {editingApplicationId ? 'Сохранить application' : 'Добавить application'}
                </Button>
              </Stack>
              <Stack spacing={0.75}>
                {tenantApplications.length ? tenantApplications.map((application) => (
                  <Box
                    key={application.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 0.8fr 1fr auto auto' },
                      gap: 1,
                      alignItems: 'center',
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      border: '1px solid rgba(38, 65, 94, 0.10)',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{application.name}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{application.external_application_id}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {application.system_type || 'Любой тип'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {application.decoder || 'Любой decoder'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {application.manufacturer || 'Любой производитель'}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {application.is_default && <Chip size="small" label="Default" variant="outlined" />}
                      <Chip
                        size="small"
                        label={application.sync_enabled ? 'Включено' : 'Выключено'}
                        color={application.sync_enabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Редактировать">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleEditApplication(application)}
                          disabled={saving}
                          sx={{ minWidth: 36, px: 1 }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleDeleteApplication(application.id)}
                          disabled={saving}
                          sx={{ minWidth: 36, px: 1 }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Box>
                )) : (
                  <Typography variant="body2" color="text.secondary">
                    Applications пока не добавлены.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setBindingDialog(null)} disabled={saving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleSaveBinding} disabled={saving}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoRaWanSettingsPage;
